import React, { useState, useEffect, useRef } from "react";
import {
  Modal, Form, Input, DatePicker, Select, Button,
  message, Popconfirm, Spin, Tag, Empty,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, EditOutlined, CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import api from "../services/api";

dayjs.extend(customParseFormat); // necessário para parseDataSegura (parse com formato estrito)

const { Option } = Select;
const { TextArea } = Input;

// ── Hook de responsividade ──────────────────────────────────────────────
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    handler();
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

// ── Rotas ────────────────────────────────────────────────────────────────
const ROTA_PESQUISAR  = "/ServerPrincipal/PesquisaTaferas";
const ROTA_INSERIR    = "/ServerPrincipal/InserirTarefa";
const ROTA_ATUALIZAR  = "/ServerPrincipal/AtualizarTarefa";
const ROTA_DELETAR    = "/ServerPrincipal/DeletarTarefa";

// Campos confirmados no FDQuery
const CAMPO_ID     = "taf_id";
const CAMPO_STATUS = "taf_status";

const COLUNAS = [
  { key: "A Fazer",   titulo: "A Fazer",    cor: "#faad14" },
  { key: "Fazendo",   titulo: "Fazendo",    cor: "#1677ff" },
  { key: "Concluido", titulo: "Concluído",  cor: "#52c41a" },
];

const FORMATO_DATA = "DD/MM/YYYY";

// ── Parsing (agora o backend devolve JSON limpo: um array de objetos) ─────
const extractRows = (data) => {
  if (Array.isArray(data)) return data;
  if (data && data.Table && Array.isArray(data.Table)) return data.Table;
  return [];
};

const normalize = (row) => {
  if (!row || typeof row !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(row)) out[k.toLowerCase()] = v;
  return out;
};

const parseDataSegura = (valor) => {
  if (!valor) return dayjs();
  const tentativas = [FORMATO_DATA, "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"];
  for (const fmt of tentativas) {
    const d = dayjs(valor, fmt, true);
    if (d.isValid()) return d;
  }
  const fallback = dayjs(valor);
  return fallback.isValid() ? fallback : dayjs();
};

const mapTarefa = (row) => ({
  id:        row[CAMPO_ID.toLowerCase()],
  nome:      row.taf_nome ?? "",
  descricao: row.taf_descricao ?? "",
  data:      row.taf_data ?? "",
  status:    row[CAMPO_STATUS.toLowerCase()] ?? COLUNAS[0].key,
});

export const Tarefas = ({ clienteId: clienteIdProp }) => {
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [clienteId, setClienteId]       = useState(null);
  const [tarefas, setTarefas]           = useState([]);
  const [modalAberto, setModalAberto]   = useState(false);
  const [tarefaEditando, setTarefaEditando] = useState(null);

  // Estado visual do drag
  const [dragId, setDragId]         = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [ghost, setGhost]           = useState(null); // preview flutuante {x,y,nome}

  // Refs para evitar closures obsoletas dentro dos listeners globais de pointer
  const tarefasRef        = useRef([]);
  const startRef          = useRef(null);   // {x, y, id, nome}
  const activeRef         = useRef(false);  // drag ultrapassou o threshold?
  const dragOverColRef    = useRef(null);
  const dropHandlerRef    = useRef(null);

  useEffect(() => { tarefasRef.current = tarefas; }, [tarefas]);

  useEffect(() => {
    let id = clienteIdProp;
    if (!id) {
      try { const u = JSON.parse(localStorage.getItem("user") || "{}"); id = u.cli_id || u.id; } catch (e) {}
    }
    if (!id) id = parseInt(localStorage.getItem("userID") || "0") || null;
    if (id) { setClienteId(id); carregarTarefas(id); }
    else message.error("Usuário não autenticado.");
  }, [clienteIdProp]);

  const carregarTarefas = async (id) => {
    setLoading(true);
    try {
      const res  = await api.get(ROTA_PESQUISAR, { params: { Login: id } });
      const rows = extractRows(res.data).map(normalize).map(mapTarefa);
      setTarefas(rows);
    } catch (e) {
      message.error("Erro ao carregar tarefas.");
      console.error("[Tarefas] carregarTarefas →", e?.response?.data || e?.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Criar / Editar ─────────────────────────────────────────────────────
  const abrirNovaTarefa = (statusInicial) => {
    setTarefaEditando(null);
    form.resetFields();
    form.setFieldsValue({ status: statusInicial || COLUNAS[0].key, data: dayjs() });
    setModalAberto(true);
  };

  const abrirEdicao = (tarefa) => {
    setTarefaEditando(tarefa);
    form.setFieldsValue({
      nome: tarefa.nome,
      descricao: tarefa.descricao,
      data: parseDataSegura(tarefa.data),
      status: tarefa.status,
    });
    setModalAberto(true);
  };

  const handleSalvar = async (values) => {
    setSaving(true);
    try {
      const payload = {
        Nome:      values.nome,
        Descricao: values.descricao || "",
        Data:      values.data.format(FORMATO_DATA),
        Status:    values.status,
      };
      if (tarefaEditando) {
        await api.post(ROTA_ATUALIZAR, { ...payload, IDTarefa: tarefaEditando.id });
        message.success("Tarefa atualizada com sucesso!");
      } else {
        await api.post(ROTA_INSERIR, payload);
        message.success("Tarefa criada com sucesso!");
      }
      setModalAberto(false);
      form.resetFields();
      carregarTarefas(clienteId);
    } catch (e) {
      const errMsg = e?.response?.data || e?.message || "Erro ao salvar tarefa";
      message.error(String(errMsg));
      console.error("[Tarefas] handleSalvar →", e?.response?.status, e?.response?.data);
    } finally {
      setSaving(false);
    }
  };

  // ── Excluir ─────────────────────────────────────────────────────────────
  const handleExcluir = async (tarefa) => {
    const anteriores = tarefas;
    setTarefas((prev) => prev.filter((t) => t.id !== tarefa.id));
    try {
      await api.post(ROTA_DELETAR, { IDTarefa: tarefa.id });
      message.success("Tarefa excluída.");
    } catch (e) {
      message.error("Não foi possível excluir a tarefa.");
      setTarefas(anteriores);
      console.error("[Tarefas] handleExcluir →", e?.response?.data || e?.message);
    }
  };

  // ── Mover de coluna (usado pelo drop) ───────────────────────────────────
  const handleDropColuna = async (statusKey, idAtual) => {
    if (!idAtual || !statusKey) return;
    const tarefa = tarefasRef.current.find((t) => t.id === idAtual);
    if (!tarefa || tarefa.status === statusKey) return;

    const statusAnterior = tarefa.status;
    setTarefas((prev) => prev.map((t) => (t.id === idAtual ? { ...t, status: statusKey } : t))); // otimista

    try {
      await api.post(ROTA_ATUALIZAR, {
        IDTarefa:  tarefa.id,
        Nome:      tarefa.nome,
        Descricao: tarefa.descricao,
        Data:      typeof tarefa.data === "string" && tarefa.data.includes("/")
          ? tarefa.data
          : parseDataSegura(tarefa.data).format(FORMATO_DATA),
        Status:    statusKey,
      });
    } catch (e) {
      message.error("Não foi possível mover a tarefa. Revertendo.");
      setTarefas((prev) => prev.map((t) => (t.id === idAtual ? { ...t, status: statusAnterior } : t)));
      console.error("[Tarefas] handleDropColuna →", e?.response?.data || e?.message);
    }
  };
  // mantém a referência sempre atualizada para o listener global chamar
  dropHandlerRef.current = handleDropColuna;

  // ── Drag-and-drop via Pointer Events (funciona no mouse E no touch) ─────
  const onCardPointerDown = (e, tarefa) => {
    // registra o ponto inicial; o drag só "arma" após passar do threshold
    startRef.current = { x: e.clientX, y: e.clientY, id: tarefa.id, nome: tarefa.nome };
  };

  useEffect(() => {
    const move = (e) => {
      const st = startRef.current;
      if (!st) return;

      if (!activeRef.current) {
        const dist = Math.hypot(e.clientX - st.x, e.clientY - st.y);
        if (dist < 6) return; // ainda é um clique/tap, não um arraste
        activeRef.current = true;
        setDragId(st.id);
      }

      e.preventDefault(); // impede scroll da página enquanto arrasta (touch)
      setGhost({ x: e.clientX, y: e.clientY, nome: st.nome });

      const el  = document.elementFromPoint(e.clientX, e.clientY);
      const col = el?.closest?.("[data-col]")?.getAttribute("data-col") ?? null;
      dragOverColRef.current = col;
      setDragOverCol(col);
    };

    const up = () => {
      const st        = startRef.current;
      const wasActive = activeRef.current;
      const targetCol = dragOverColRef.current;
      const id        = st?.id;

      startRef.current = null;
      activeRef.current = false;
      dragOverColRef.current = null;
      setDragId(null);
      setDragOverCol(null);
      setGhost(null);

      if (wasActive && targetCol && id) dropHandlerRef.current?.(targetCol, id);
    };

    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, []);

  const larguraColuna = isMobile ? "88vw" : 300;

  const s = {
    wrap:    { padding: isMobile ? "16px 12px" : "20px 24px" },
    header:  { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 10 },
    titulo:  { fontSize: 18, fontWeight: 700, color: "#222", margin: 0 },
    board:   { display: "flex", gap: 16, alignItems: "flex-start", overflowX: "auto", paddingBottom: 8, scrollSnapType: isMobile ? "x mandatory" : "none" },
    coluna:  (ativa) => ({
      flex: `0 0 ${typeof larguraColuna === "number" ? larguraColuna + "px" : larguraColuna}`,
      background: ativa ? "#eef4ff" : "#f5f6f8",
      border: ativa ? "2px dashed #1677ff" : "2px dashed transparent",
      borderRadius: 8,
      padding: 12,
      minHeight: 200,
      transition: "background 0.15s, border 0.15s",
      scrollSnapAlign: isMobile ? "start" : "none",
    }),
    colHeader: (cor) => ({
      display: "flex", alignItems: "center", justifyContent: "space-between",
      marginBottom: 10, paddingBottom: 8, borderBottom: `2px solid ${cor}`,
    }),
    colTitulo: { fontSize: 13, fontWeight: 700, color: "#333", display: "flex", alignItems: "center", gap: 6 },
    contador: (cor) => ({
      fontSize: 11, fontWeight: 700, color: "#fff", background: cor,
      borderRadius: 10, padding: "1px 8px", minWidth: 20, textAlign: "center",
    }),
    card: (arrastando) => ({
      background: "#fff", borderRadius: 6, padding: "10px 12px", marginBottom: 8,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: "1px solid #eee",
      cursor: "grab", opacity: arrastando ? 0.4 : 1, transition: "opacity 0.15s",
      touchAction: "none", // essencial para o drag por toque não virar scroll
    }),
    cardTitulo: { fontSize: 13, fontWeight: 600, color: "#222", marginBottom: 4 },
    cardDesc:   { fontSize: 12, color: "#777", marginBottom: 8, whiteSpace: "pre-wrap" },
    cardFooter: { display: "flex", alignItems: "center", justifyContent: "space-between" },
    cardData:   { fontSize: 11, color: "#999", display: "flex", alignItems: "center", gap: 4 },
    cardAcoes:  { display: "flex", gap: 4 },
    addBtn: {
      width: "100%", marginTop: 4, height: 32, border: "1px dashed #d9d9d9",
      borderRadius: 6, background: "transparent", color: "#888", fontSize: 12,
    },
  };

  const pararDrag = (e) => e.stopPropagation(); // botões não devem iniciar arraste

  return (
    <Spin spinning={loading}>
      <div style={s.wrap}>
        <div style={s.header}>
          <h2 style={s.titulo}>Tarefas</h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => abrirNovaTarefa()}
            style={{ background: "#000000", borderColor: "#000000" }}
          >
            Nova Tarefa
          </Button>
        </div>

        <div style={s.board}>
          {COLUNAS.map((coluna) => {
            const tarefasColuna = tarefas.filter((t) => t.status === coluna.key);
            return (
              <div key={coluna.key} data-col={coluna.key} style={s.coluna(dragOverCol === coluna.key)}>
                <div style={s.colHeader(coluna.cor)}>
                  <span style={s.colTitulo}>{coluna.titulo}</span>
                  <span style={s.contador(coluna.cor)}>{tarefasColuna.length}</span>
                </div>

                {tarefasColuna.length === 0 ? (
                  <Empty
                    description="Sem tarefas"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ margin: "20px 0 12px" }}
                  />
                ) : (
                  tarefasColuna.map((tarefa) => (
                    <div
                      key={tarefa.id}
                      onPointerDown={(e) => onCardPointerDown(e, tarefa)}
                      style={s.card(dragId === tarefa.id)}
                    >
                      <div style={s.cardTitulo}>{tarefa.nome}</div>
                      {tarefa.descricao && <div style={s.cardDesc}>{tarefa.descricao}</div>}
                      <div style={s.cardFooter}>
                        <span style={s.cardData}>
                          <CalendarOutlined />
                          {parseDataSegura(tarefa.data).format(FORMATO_DATA)}
                        </span>
                        <div style={s.cardAcoes}>
                          <Button
                            type="text" size="small" icon={<EditOutlined />}
                            onPointerDown={pararDrag}
                            onClick={() => abrirEdicao(tarefa)}
                          />
                          <Popconfirm
                            title="Excluir esta tarefa?"
                            okText="Excluir" cancelText="Cancelar"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleExcluir(tarefa)}
                          >
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} onPointerDown={pararDrag} />
                          </Popconfirm>
                        </div>
                      </div>
                    </div>
                  ))
                )}

                <button style={s.addBtn} onPointerDown={pararDrag} onClick={() => abrirNovaTarefa(coluna.key)}>
                  <PlusOutlined /> Adicionar tarefa
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Preview flutuante enquanto arrasta */}
      {ghost && (
        <div
          style={{
            position: "fixed", left: ghost.x + 12, top: ghost.y + 12, zIndex: 9999,
            pointerEvents: "none", background: "#fff", border: "1px solid #1677ff",
            borderRadius: 6, padding: "6px 10px", fontSize: 13, fontWeight: 600,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)", maxWidth: 220,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {ghost.nome}
        </div>
      )}

      <Modal
        title={tarefaEditando ? "Editar Tarefa" : "Nova Tarefa"}
        open={modalAberto}
        onCancel={() => setModalAberto(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText={tarefaEditando ? "Salvar" : "Criar"}
        cancelText="Cancelar"
        destroyOnClose
        width={isMobile ? "92vw" : 520}
      >
        <Form form={form} layout="vertical" onFinish={handleSalvar}>
          <Form.Item name="nome" label="Nome" rules={[{ required: true, message: "Informe o nome da tarefa." }]}>
            <Input placeholder="Ex: Ligar para o cliente" />
          </Form.Item>
          <Form.Item name="descricao" label="Descrição">
            <TextArea rows={3} placeholder="Detalhes da tarefa (opcional)" />
          </Form.Item>
          <Form.Item name="data" label="Data" rules={[{ required: true, message: "Informe a data." }]}>
            <DatePicker style={{ width: "100%" }} format={FORMATO_DATA} />
          </Form.Item>
          <Form.Item name="status" label="Coluna" rules={[{ required: true }]}>
            <Select>
              {COLUNAS.map((c) => (
                <Option key={c.key} value={c.key}>
                  <Tag color={c.cor}>{c.titulo}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Spin>
  );
};

export default Tarefas;